// @flow

import _get from 'lodash/get';
import { createSelector, type OutputSelector } from 'reselect';

import { selectIsSamlUser } from '@gandi/authentication';
import {
  selectCartOwnerList,
  selectHasOwnerOnly,
  selectIsLoadedCartOwnerList,
  selectRawProductsArray,
  selectCartProductOwner,
  type Product,
} from '@gandi/cart';
import { selectCurrentId, selectPersonalId } from '@gandi/redux-sharing';

import {
  selectHasMissingOrgInfo,
  selectShopCurrent,
} from '../../../modules/organization/selectors';
import type { FullState } from '../../../types';
import type { CartOwnerCandidate } from '../types';

import {
  mustCartOwnerBeReseller,
  canChooseProductOwner,
  isProductAutorenewable,
  canChooseProductContacts,
} from './helpers';

const EMPTY_ARRAY: Array<void> = [];

export const selectCartOwners: (state: FullState) => Array<CartOwnerCandidate> =
  createSelector(
    [selectCurrentId, selectPersonalId, selectCartOwnerList],
    (currentId, personalId, ownerList) =>
      // $FlowIgnore[incompatible-call]
      (ownerList || EMPTY_ARRAY).reduce((acc, org) => {
        // $FlowFixMe[method-unbinding]
        (org.id === currentId ? acc.unshift : acc.push).call(acc, {
          ...org,
          personal: org.id === personalId,
        });
        return acc;
      }, EMPTY_ARRAY.slice()),
  );

export const selectShouldRedirectToAccountForm = (
  state: FullState,
): boolean => {
  const personalId = selectPersonalId(state);
  const sharingId = selectCurrentId(state);
  const ownerOnly = selectHasOwnerOnly(state);
  const cartOwnerList = selectCartOwners(state);
  const canPay = cartOwnerList.some(item => item.id === sharingId);
  const missingOrgInfo = selectHasMissingOrgInfo(state, { orgId: sharingId });

  return (
    personalId === sharingId &&
    cartOwnerList.length === 1 &&
    canPay &&
    !ownerOnly &&
    !!missingOrgInfo
  );
};

export const selectResellerOwnerId: OutputSelector<FullState, mixed, any> =
  createSelector([selectCartOwnerList], owners =>
    _get(
      owners.find(item => item.reseller),
      'id',
      '',
    ),
  );

const selectCurrentOrgIsReseller: OutputSelector<FullState, mixed, boolean> =
  createSelector(
    [selectResellerOwnerId, selectCurrentId],
    (resellerId, currentId) =>
      !!(resellerId && currentId && resellerId === currentId),
  );

export const selectShouldRedirectToResellerOwnerId: OutputSelector<
  FullState,
  mixed,
  boolean,
> = createSelector(
  [selectHasOwnerOnly, selectCartOwnerList, selectCurrentOrgIsReseller],
  (hasOwnerOnly, cartOwnerList, currentOrgIsReseller) =>
    mustCartOwnerBeReseller(hasOwnerOnly, cartOwnerList) &&
    !currentOrgIsReseller,
);

export const selectRedirectToNonMinimal = (
  state: FullState,
): false | string => {
  const hasOwnerOnly = selectHasOwnerOnly(state);
  // Short-cut, only the product's owner is billable.
  if (hasOwnerOnly) {
    return false;
  }

  const personalId = selectPersonalId(state);
  const currentId = selectCurrentId(state);
  const isSamlUser = selectIsSamlUser(state);
  // Assert current organization is personal.
  if (personalId !== currentId) {
    return false;
  }

  const missingOrgInfo = selectHasMissingOrgInfo(state);
  // Assert current organization is minimal.
  if (!missingOrgInfo) {
    return false;
  }

  const cartOwnerList = selectCartOwnerList(state);
  // Assert cart owners list includes the current organization.
  if (!isSamlUser && !cartOwnerList.some(owner => owner.id === currentId)) {
    return false;
  }

  const candidate = cartOwnerList.find(
    owner => owner.type !== 'individual' && owner.id !== currentId,
  );
  // Assert cart owners list includes at least one non-individual org.
  if (!candidate) {
    return false;
  }

  // Return the candidate's sharing_id, to redirect user to this context.
  return candidate.id;
};

export const selectCurrentOrganizationCanPay: OutputSelector<
  FullState,
  mixed,
  boolean,
> = createSelector(
  [selectIsLoadedCartOwnerList, selectCartOwners, selectCurrentId],
  (loaded, list, id) => loaded && list.some(item => item.id === id),
);

/**
 * Tell if some procduct are eligible for ownership delegation
 * The checking is only based on the product type and process
 */
export const selectCanChooseProductOwner: OutputSelector<
  FullState,
  any,
  boolean,
> = createSelector([selectRawProductsArray], products =>
  products.some(canChooseProductOwner),
);

export const selectCanChangeProductContacts: OutputSelector<
  FullState,
  any,
  boolean,
> = createSelector([selectRawProductsArray], products =>
  products.some(canChooseProductContacts),
);

// TODO: this should modify the cart configuration instead
export function selectProductsToUpdateForAutorenew(
  state: FullState,
): Array<Product> | Array<any> {
  const products = selectRawProductsArray(state);

  const autorenewable = products.filter(isProductAutorenewable);

  if (!autorenewable.length) {
    return [];
  }

  // Some products need to be checked for potentially necessary update.
  const current = selectShopCurrent(state, true);

  const productOwner = selectCartProductOwner(state);
  const autorenew =
    productOwner && productOwner !== current?.id ? false : undefined;

  return autorenewable.reduce<Array<Product>>((acc, product) => {
    let updated = product;

    if (isProductAutorenewable(product)) {
      if (product.payload.autorenew !== autorenew) {
        updated = {
          ...updated,
          payload: {
            ...updated.payload,
            autorenew,
          },
        };
      }
    }

    if (updated !== product) {
      acc.push(updated);
    }

    return acc;
  }, []);
}
